
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import { BASE_URL } from '@/config/api';
import { useAuth } from '@/contexts/AuthContext';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ArrowLeft, User, Calendar, Clock, FileText, Edit, ChevronRight, BarChart, Book, Image } from 'lucide-react';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { format } from 'date-fns';

interface Student {
  id: string;
  firstname: string;
  lastname: string;
  email?: string;
  phone?: string;
  mobile?: string;
  mother_email?: string;
  mother_phone?: string;
  father_email?: string;
  father_phone?: string;
  profile_picture?: string;
  user_account?: boolean;
}

interface Lesson {
  id: string;
  title: string;
  date: string;
  time: string;
  location: string;
  status: 'upcoming' | 'past' | 'canceled';
}

interface Invoice {
  id: string;
  number: string;
  date: string;
  amount: number;
  status: 'paid' | 'unpaid' | 'overdue';
}

interface Stats {
  totalLessons: number;
  completedLessons: number;
  upcomingLessons: number;
  canceledLessons: number;
  totalHours: number;
  averageRating: number;
  attendanceRate: number;
  progress: number;
}

const StudentDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { token } = useAuth();
  const { toast } = useToast();
  const [student, setStudent] = useState<Student | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<Partial<Student>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingStudent, setIsLoadingStudent] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [stats, setStats] = useState<Stats>({
    totalLessons: 32,
    completedLessons: 24,
    upcomingLessons: 6,
    canceledLessons: 2,
    totalHours: 48,
    averageRating: 4.7,
    attendanceRate: 95,
    progress: 75
  });
  
  // Mock data - in a real app, this would come from the API
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);

  const fetchStudent = async () => {
    if (!id) return;
    
    setIsLoadingStudent(true);
    try {
      const response = await fetch(`${BASE_URL}/api/student/${id}`, {
        headers: {
          'Accept' : 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      
      if (response.ok && data.status === 1) {
        setStudent(data.student);
        setFormData(data.student);
      } else {
        toast({
          title: "Student not found",
          description: data.message || "The requested student could not be found.",
          variant: "destructive"
        });
        navigate('/settings');
      }
    } catch (error) {
      console.error('Error fetching student:', error);
      toast({
        title: "Error loading student",
        description: "There was a problem fetching the student details.",
        variant: "destructive"
      });
      navigate('/settings');
    } finally {
      setIsLoadingStudent(false);
    }
  };

  useEffect(() => {
    fetchStudent();
    // Mock data loading - in a real app, this would be API calls
    loadMockData();
  }, [id, token]);

  const loadMockData = () => {
    // Mock lessons data
    setLessons([
      { id: '1', title: 'Tennis Lesson', date: '2023-07-15', time: '14:00-15:00', location: 'Court 1', status: 'past' },
      { id: '2', title: 'Tennis Lesson', date: '2023-07-22', time: '14:00-15:00', location: 'Court 2', status: 'past' },
      { id: '3', title: 'Tennis Lesson', date: '2023-07-29', time: '14:00-15:00', location: 'Court 1', status: 'past' },
      { id: '4', title: 'Tennis Lesson', date: '2023-08-05', time: '14:00-15:00', location: 'Court 3', status: 'upcoming' },
      { id: '5', title: 'Tennis Lesson', date: '2023-08-12', time: '14:00-15:00', location: 'Court 2', status: 'upcoming' }
    ]);
    
    // Mock invoices data
    setInvoices([
      { id: '1', number: 'INV-001', date: '2023-06-01', amount: 150, status: 'paid' },
      { id: '2', number: 'INV-002', date: '2023-07-01', amount: 150, status: 'paid' },
      { id: '3', number: 'INV-003', date: '2023-08-01', amount: 150, status: 'unpaid' }
    ]);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!student) return;
    
    setIsLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/api/student/update`, {
        method: 'POST',
        headers: {
          'Accept' : 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          id: student.id,
          ...formData
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update student');
      }

      // Refresh student data
      fetchStudent();
      
      toast({
        title: "Student updated",
        description: `${formData.firstname} ${formData.lastname} has been updated`
      });
      
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating student:', error);
      toast({
        title: "Failed to update student",
        description: "There was an error updating the student. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'upcoming':
        return 'bg-blue-100 text-blue-800';
      case 'past':
        return 'bg-gray-100 text-gray-800';
      case 'canceled':
        return 'bg-red-100 text-red-800';
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'unpaid':
        return 'bg-yellow-100 text-yellow-800';
      case 'overdue':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return format(date, 'MMM d, yyyy');
    } catch (error) {
      return dateString;
    }
  };

  if (isLoadingStudent) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 container py-8 mt-16">
          <div className="flex items-center justify-center h-full">
            <div className="text-center space-y-4">
              <div className="animate-pulse w-16 h-16 bg-primary/20 rounded-full mx-auto"></div>
              <div className="text-lg">Loading student details...</div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!student) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 container py-8 mt-16">
          <div className="text-center">Student not found</div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />
      
      <main className="flex-1 container py-8 mt-16">
        <div className="mb-6">
          <Button variant="outline" onClick={() => navigate('/settings')} className="gap-1">
            <ArrowLeft className="h-4 w-4" />
            Back to Settings
          </Button>
        </div>
        
        {/* Hero section with student profile */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-6">
          <div className="bg-gradient-to-r from-primary/20 to-primary/10 p-8">
            <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
              <Avatar className="h-24 w-24 md:h-32 md:w-32 ring-4 ring-white">
                {student.profile_picture ? (
                  <AvatarImage src={student.profile_picture} alt={`${student.firstname} ${student.lastname}`} />
                ) : (
                  <AvatarFallback className="text-2xl bg-primary/20">
                    {student.firstname?.[0]}{student.lastname?.[0]}
                  </AvatarFallback>
                )}
              </Avatar>
              
              <div className="flex-1 text-center md:text-left">
                <h1 className="text-3xl font-bold">{student.firstname} {student.lastname}</h1>
                {student.email && (
                  <p className="text-muted-foreground mt-1">{student.email}</p>
                )}
                
                <div className="mt-4 flex flex-wrap gap-2 justify-center md:justify-start">
                  <Badge variant="outline" className="font-normal">
                    {student.user_account ? 'Has User Account' : 'No User Account'}
                  </Badge>
                  
                  <Badge variant="outline" className="font-normal">
                    {stats.totalHours} Total Hours
                  </Badge>
                  
                  <Badge variant="outline" className="font-normal">
                    {stats.totalLessons} Lessons
                  </Badge>
                </div>
              </div>
              
              <Button onClick={() => setIsEditing(true)} className="gap-2">
                <Edit className="h-4 w-4" />
                Edit Profile
              </Button>
            </div>
          </div>
        </div>
        
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList className="grid grid-cols-4 md:w-auto">
            <TabsTrigger value="overview" className="flex items-center gap-2">
              <User className="h-4 w-4 hidden md:block" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="lessons" className="flex items-center gap-2">
              <Calendar className="h-4 w-4 hidden md:block" />
              Lessons
            </TabsTrigger>
            <TabsTrigger value="invoices" className="flex items-center gap-2">
              <FileText className="h-4 w-4 hidden md:block" />
              Invoices
            </TabsTrigger>
            <TabsTrigger value="stats" className="flex items-center gap-2">
              <BarChart className="h-4 w-4 hidden md:block" />
              Statistics
            </TabsTrigger>
          </TabsList>
          
          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Personal Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="h-5 w-5 text-primary" />
                    Personal Information
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {isEditing ? (
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="firstname">First Name</Label>
                        <Input
                          id="firstname"
                          name="firstname"
                          value={formData.firstname || ''}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="lastname">Last Name</Label>
                        <Input
                          id="lastname"
                          name="lastname"
                          value={formData.lastname || ''}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email || ''}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone</Label>
                        <Input
                          id="phone"
                          name="phone"
                          value={formData.phone || ''}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="mobile">Mobile</Label>
                        <Input
                          id="mobile"
                          name="mobile"
                          value={formData.mobile || ''}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button type="submit" disabled={isLoading}>
                          Save Changes
                        </Button>
                        <Button type="button" variant="outline" onClick={() => setIsEditing(false)}>
                          Cancel
                        </Button>
                      </div>
                    </form>
                  ) : (
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 gap-2">
                        {student.email && (
                          <div>
                            <div className="text-xs text-muted-foreground">Email</div>
                            <div className="font-medium">{student.email}</div>
                          </div>
                        )}
                        {student.phone && (
                          <div>
                            <div className="text-xs text-muted-foreground">Phone</div>
                            <div className="font-medium">{student.phone}</div>
                          </div>
                        )}
                        {student.mobile && (
                          <div>
                            <div className="text-xs text-muted-foreground">Mobile</div>
                            <div className="font-medium">{student.mobile}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* Parent Contact Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="h-5 w-5 text-primary" />
                    Parent Information
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {isEditing ? (
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="mother_email">Mother's Email</Label>
                        <Input
                          id="mother_email"
                          name="mother_email"
                          type="email"
                          value={formData.mother_email || ''}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="mother_phone">Mother's Phone</Label>
                        <Input
                          id="mother_phone"
                          name="mother_phone"
                          value={formData.mother_phone || ''}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="father_email">Father's Email</Label>
                        <Input
                          id="father_email"
                          name="father_email"
                          type="email"
                          value={formData.father_email || ''}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="father_phone">Father's Phone</Label>
                        <Input
                          id="father_phone"
                          name="father_phone"
                          value={formData.father_phone || ''}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 gap-2">
                        {student.mother_email && (
                          <div>
                            <div className="text-xs text-muted-foreground">Mother's Email</div>
                            <div className="font-medium">{student.mother_email}</div>
                          </div>
                        )}
                        {student.mother_phone && (
                          <div>
                            <div className="text-xs text-muted-foreground">Mother's Phone</div>
                            <div className="font-medium">{student.mother_phone}</div>
                          </div>
                        )}
                        {student.father_email && (
                          <div>
                            <div className="text-xs text-muted-foreground">Father's Email</div>
                            <div className="font-medium">{student.father_email}</div>
                          </div>
                        )}
                        {student.father_phone && (
                          <div>
                            <div className="text-xs text-muted-foreground">Father's Phone</div>
                            <div className="font-medium">{student.father_phone}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* Summary Stats */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart className="h-5 w-5 text-primary" />
                    Student Overview
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <div className="text-xs text-muted-foreground">Overall Progress</div>
                    <Progress value={stats.progress} className="h-2 mt-1.5" />
                    <div className="text-right text-xs text-muted-foreground mt-1">{stats.progress}%</div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 pt-2">
                    <div>
                      <div className="text-xs text-muted-foreground">Total Lessons</div>
                      <div className="font-medium text-lg">{stats.totalLessons}</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Completed</div>
                      <div className="font-medium text-lg">{stats.completedLessons}</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Upcoming</div>
                      <div className="font-medium text-lg">{stats.upcomingLessons}</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Total Hours</div>
                      <div className="font-medium text-lg">{stats.totalHours}</div>
                    </div>
                  </div>
                  
                  <Separator />
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-xs text-muted-foreground">Attendance Rate</div>
                      <div className="font-medium text-lg">{stats.attendanceRate}%</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Average Rating</div>
                      <div className="font-medium text-lg">{stats.averageRating}/5</div>
                    </div>
                  </div>
                </CardContent>
                <CardFooter>
                  <Button variant="outline" className="w-full" onClick={() => setActiveTab("stats")}>
                    View Detailed Statistics <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </CardFooter>
              </Card>
              
              {/* Upcoming Lessons */}
              <Card className="md:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-primary" />
                    Upcoming Lessons
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Time</TableHead>
                          <TableHead>Title</TableHead>
                          <TableHead>Location</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {lessons.filter(lesson => lesson.status === 'upcoming').map((lesson) => (
                          <TableRow key={lesson.id}>
                            <TableCell>{formatDate(lesson.date)}</TableCell>
                            <TableCell>{lesson.time}</TableCell>
                            <TableCell>{lesson.title}</TableCell>
                            <TableCell>{lesson.location}</TableCell>
                            <TableCell>
                              <Badge className={getStatusColor(lesson.status)}>
                                {lesson.status.charAt(0).toUpperCase() + lesson.status.slice(1)}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                        {lessons.filter(lesson => lesson.status === 'upcoming').length === 0 && (
                          <TableRow>
                            <TableCell colSpan={5} className="text-center py-4">No upcoming lessons found for this student</TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
                <CardFooter>
                  <Button variant="outline" className="w-full" onClick={() => setActiveTab("lessons")}>
                    View All Lessons <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </CardFooter>
              </Card>
              
              {/* Recent Invoices */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-primary" />
                    Recent Invoices
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Number</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Amount</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {invoices.slice(0, 3).map((invoice) => (
                          <TableRow key={invoice.id}>
                            <TableCell>{invoice.number}</TableCell>
                            <TableCell>{formatDate(invoice.date)}</TableCell>
                            <TableCell>${invoice.amount.toFixed(2)}</TableCell>
                            <TableCell>
                              <Badge className={getStatusColor(invoice.status)}>
                                {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                        {invoices.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={4} className="text-center py-4">No invoices found for this student</TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
                <CardFooter>
                  <Button variant="outline" className="w-full" onClick={() => setActiveTab("invoices")}>
                    View All Invoices <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </CardFooter>
              </Card>
            </div>
          </TabsContent>
          
          {/* Lessons Tab */}
          <TabsContent value="lessons">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-primary" />
                    Lessons History
                  </CardTitle>
                  <CardDescription>
                    A complete record of all {student.firstname}'s lessons
                  </CardDescription>
                </div>
                <Button variant="outline">
                  Schedule a Lesson
                </Button>
              </CardHeader>
              <CardContent>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Time</TableHead>
                        <TableHead>Title</TableHead>
                        <TableHead>Location</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {lessons.map((lesson) => (
                        <TableRow key={lesson.id}>
                          <TableCell>{formatDate(lesson.date)}</TableCell>
                          <TableCell>{lesson.time}</TableCell>
                          <TableCell>{lesson.title}</TableCell>
                          <TableCell>{lesson.location}</TableCell>
                          <TableCell>
                            <Badge className={getStatusColor(lesson.status)}>
                              {lesson.status.charAt(0).toUpperCase() + lesson.status.slice(1)}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Button variant="ghost" size="sm">View</Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      {lessons.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center py-4">No lessons found for this student</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Invoices Tab */}
          <TabsContent value="invoices">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-primary" />
                    Invoices
                  </CardTitle>
                  <CardDescription>
                    Manage and track all financial transactions
                  </CardDescription>
                </div>
                <Button variant="outline">
                  Create New Invoice
                </Button>
              </CardHeader>
              <CardContent>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Invoice #</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {invoices.map((invoice) => (
                        <TableRow key={invoice.id}>
                          <TableCell>{invoice.number}</TableCell>
                          <TableCell>{formatDate(invoice.date)}</TableCell>
                          <TableCell>${invoice.amount.toFixed(2)}</TableCell>
                          <TableCell>
                            <Badge className={getStatusColor(invoice.status)}>
                              {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Button variant="ghost" size="sm" onClick={() => navigate(`/invoices/${invoice.id}`)}>
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      {invoices.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center py-4">No invoices found for this student</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Statistics Tab */}
          <TabsContent value="stats">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart className="h-5 w-5 text-primary" />
                  Detailed Statistics
                </CardTitle>
                <CardDescription>
                  Comprehensive performance metrics for {student.firstname}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* Left column stats */}
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-medium mb-4">Lesson Breakdown</h3>
                      <div className="space-y-3">
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span>Completed Lessons</span>
                            <span className="font-medium">{stats.completedLessons}</span>
                          </div>
                          <Progress value={(stats.completedLessons / stats.totalLessons) * 100} className="h-2" />
                        </div>
                        
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span>Upcoming Lessons</span>
                            <span className="font-medium">{stats.upcomingLessons}</span>
                          </div>
                          <Progress value={(stats.upcomingLessons / stats.totalLessons) * 100} className="h-2" />
                        </div>
                        
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span>Canceled Lessons</span>
                            <span className="font-medium">{stats.canceledLessons}</span>
                          </div>
                          <Progress value={(stats.canceledLessons / stats.totalLessons) * 100} className="h-2" />
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="text-lg font-medium mb-4">Performance</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <Card>
                          <CardContent className="pt-6">
                            <div className="text-center">
                              <div className="text-3xl font-bold">{stats.attendanceRate}%</div>
                              <div className="text-sm text-muted-foreground mt-1">Attendance Rate</div>
                            </div>
                          </CardContent>
                        </Card>
                        
                        <Card>
                          <CardContent className="pt-6">
                            <div className="text-center">
                              <div className="text-3xl font-bold">{stats.averageRating}/5</div>
                              <div className="text-sm text-muted-foreground mt-1">Average Rating</div>
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    </div>
                  </div>
                  
                  {/* Right column stats */}
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-medium mb-4">Learning Progress</h3>
                      <div className="space-y-6">
                        <div>
                          <div className="flex justify-between mb-1">
                            <span className="text-sm">Overall Progress</span>
                            <span className="text-sm font-medium">{stats.progress}%</span>
                          </div>
                          <Progress value={stats.progress} className="h-2" />
                        </div>
                        
                        <Card>
                          <CardContent className="p-6">
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="text-3xl font-bold">{stats.totalHours}</div>
                                <div className="text-sm text-muted-foreground mt-1">Total Hours</div>
                              </div>
                              <Clock className="h-9 w-9 text-muted-foreground/50" />
                            </div>
                          </CardContent>
                        </Card>
                        
                        <Card>
                          <CardContent className="p-6">
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="text-3xl font-bold">{stats.totalLessons}</div>
                                <div className="text-sm text-muted-foreground mt-1">Total Lessons</div>
                              </div>
                              <Book className="h-9 w-9 text-muted-foreground/50" />
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
      
      <Footer />
    </div>
  );
};

export default StudentDetail;
